/**
 * PillButton - Pill-shaped button for auth screens
 * Matches auth-screens-preview design: height 52, borderRadius 26
 */

import React from 'react';
import { TouchableOpacity, ActivityIndicator, StyleSheet, ViewStyle } from 'react-native';
import { Text } from '@gluestack-ui/themed';
import { colors } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

interface PillButtonProps {
  title: string;
  onPress: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  variant?: 'primary' | 'secondary';
  testID?: string;
  style?: ViewStyle;
}

const BASE_HEIGHT = 52;
const BASE_FONT_SIZE = 16;

export function PillButton({
  title,
  onPress,
  isLoading = false,
  isDisabled = false,
  variant = 'primary',
  testID,
  style,
}: PillButtonProps) {
  const isPrimary = variant === 'primary';
  const { fontScale, fontFamily } = useDisplayTheme();

  const scaledHeight = Math.round(BASE_HEIGHT * fontScale);

  return (
    <TouchableOpacity
      testID={testID}
      accessibilityLabel={title}
      accessibilityRole={variant === 'secondary' ? 'link' : 'button'}
      accessibilityState={{ disabled: isDisabled || isLoading }}
      onPress={onPress}
      disabled={isDisabled || isLoading}
      activeOpacity={0.7}
      style={[
        styles.button,
        { minHeight: scaledHeight, borderRadius: scaledHeight / 2 },
        isPrimary ? styles.primary : styles.secondary,
        (isDisabled || isLoading) && styles.disabled,
        style,
      ]}
    >
      {isLoading ? (
        <ActivityIndicator color={isPrimary ? 'white' : colors.primary} />
      ) : (
        <Text
          allowFontScaling={false}
          style={{
            fontSize: BASE_FONT_SIZE * fontScale,
            fontWeight: '600',
            color: isPrimary ? 'white' : colors.primary,
            ...(fontFamily ? { fontFamily } : undefined),
          }}
        >
          {title}
        </Text>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  button: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    marginBottom: 16,
  },
  primary: {
    backgroundColor: colors.primary,
  },
  secondary: {
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: colors.primary,
  },
  disabled: {
    opacity: 0.5,
  },
});
